import { useEffect, useState } from 'react'

import { api } from '../api/client'
import { Card, ErrorBanner, Spinner } from '../components/common'
import { useAsync } from '../hooks/useAsync'
import { t } from '../i18n/lingua'

/**
 * Quale traffico si conserva e quale si butta prima di occupare spazio.
 *
 * Due elenchi che rispondono a domande diverse, e vanno letti come tali:
 *
 * - **Le reti interne** dicono «il mio perimetro è questo». Sono ampie, e
 *   prepopolate con le tre RFC 1918 più le ULA IPv6 — senza queste ultime tutto
 *   il traffico IPv6 interno sparirebbe in silenzio.
 * - **Le esclusioni** dicono «questo indirizzo non mi interessa mai». Sono poche
 *   e specifiche: il backup che satura il collegamento ogni notte, il server di
 *   monitoraggio che parla con tutto.
 *
 * La conseguenza di «entrambi interni» è scritta a schermo e non lasciata
 * scoprire: butta via il traffico da e verso Internet, quindi un servizio
 * pubblicato usato **solo** da fuori sparisce dagli entry point. Chi lo scoprisse
 * dopo cercherebbe il motivo nella discovery, dove non c'è.
 */

const MODES: { value: string; label: () => string; hint: () => string }[] = [
  {
    value: 'both',
    label: () => t('Entrambi interni'),
    hint: () =>
      t('Conserva solo il traffico tra indirizzi appartenenti al perimetro, riducendo al minimo i dati raccolti. Il traffico da e verso Internet viene escluso, quindi i servizi utilizzati esclusivamente dall’esterno non vengono rilevati come entry point.'),
  },
  {
    value: 'any',
    label: () => t('Almeno uno interno'),
    hint: () =>
      t('Include il traffico da e verso Internet, mantenendo visibilità sui mittenti che accedono ai servizi pubblicati. Il volume di dati raccolti è maggiore.'),
  },
  {
    value: 'off',
    label: () => t('Nessun perimetro'),
    hint: () => t('Acquisisce tutto il traffico, mantenendo attive le esclusioni e le soglie definite sulle sonde.'),
  },
]

/** Da elenco a testo e ritorno: una riga per rete è il modo in cui si incolla. */
const toText = (values: string[]) => values.join('\n')
const toList = (text: string) =>
  text
    .split(/[\s,;]+/)
    .map((v) => v.trim())
    .filter(Boolean)

export default function FlowFilterCard() {
  const current = useAsync(() => api.flowFilter(), [])
  const [internal, setInternal] = useState('')
  const [excluded, setExcluded] = useState('')
  const [mode, setMode] = useState('both')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<unknown>(null)
  const [saved, setSaved] = useState<string | null>(null)
  const [loaded, setLoaded] = useState(false)

  useEffect(() => {
    if (current.data && !loaded) {
      setInternal(toText(current.data.internal))
      setExcluded(toText(current.data.excluded))
      setMode(current.data.mode)
      setLoaded(true)
    }
  }, [current.data, loaded])

  async function save() {
    setBusy(true)
    setError(null)
    setSaved(null)
    try {
      const result = await api.setFlowFilter(toList(excluded), toList(internal), mode)
      // Si riscrivono i campi con ciò che il server ha **normalizzato**: chi ha
      // scritto `10.1.2.3/8` deve vedere `10.0.0.0/8`, altrimenti crederebbe di
      // aver salvato una cosa diversa da quella in vigore.
      setInternal(toText(result.internal))
      setExcluded(toText(result.excluded))
      setSaved(result.warning ?? t('Filtro salvato.'))
      current.reload()
    } catch (err) {
      setError(err)
    } finally {
      setBusy(false)
    }
  }

  if (current.loading && !current.data) return <Spinner />

  const selected = MODES.find((m) => m.value === mode)

  return (
    <Card title={t('Traffico da conservare')}>
      <ErrorBanner error={current.error ?? error} />

      <p style={{ marginTop: 0 }}>
        {t('Filtra il traffico non rilevante per discovery, identificazione degli entry point e correlazione degli asset. I dati esclusi non vengono trasmessi né archiviati, riducendo traffico di rete e spazio occupato.')}
      </p>

      <div className="row">
        <div className="field">
          <label htmlFor="ff-internal">{t('Reti interne')}</label>
          <textarea
            id="ff-internal"
            rows={5}
            value={internal}
            placeholder={'10.0.0.0/8\n172.16.0.0/12\n192.168.0.0/16'}
            onChange={(e) => setInternal(e.target.value)}
          />
          <p className="field__hint">
            {t('Inserisci un indirizzo per riga; per gli indirizzi singoli /32 è facoltativo. Se l’elenco è vuoto, il filtro viene disabilitato e tutto il traffico resta acquisito, evitando esclusioni involontarie.', { suffisso: '/32' })}
            {/* La regola che evita l'incidente, detta dove si può causare. */}
            <br />
            {t("Se svuoti l'elenco il filtro si spegne invece di scartare tutto: un elenco vuoto che buttasse via ogni cosa farebbe sparire il traffico senza alcun errore.")}
          </p>
        </div>

        <div className="field">
          <label htmlFor="ff-excluded">{t('Indirizzi da escludere')}</label>
          <textarea
            id="ff-excluded"
            rows={5}
            value={excluded}
            placeholder={'10.9.0.0/16\n192.168.4.20'}
            onChange={(e) => setExcluded(e.target.value)}
          />
          <p className="field__hint">
            {t('Esclude sempre il traffico indicato, in entrambe le direzioni, anche tra asset interni.')}
          </p>
        </div>
      </div>

      <div className="field" style={{ maxWidth: 320 }}>
        <label htmlFor="ff-mode">{t('Quando una conversazione è interna')}</label>
        <select id="ff-mode" value={mode} onChange={(e) => setMode(e.target.value)}>
          {MODES.map((m) => (
            <option key={m.value} value={m.value}>
              {m.label()}
            </option>
          ))}
        </select>
        <p className="field__hint">{selected?.hint()}</p>
      </div>

      <button onClick={save} disabled={busy}>
        {busy ? t('Salvataggio…') : t('Salva filtro')}
      </button>

      {saved && <div className="banner banner--info">{saved}</div>}

      <p className="field__hint">
        {t('Vale dal prossimo giro di configurazione delle sonde e non tocca i dati già raccolti: filtrare a posteriori sarebbe una cancellazione di massa innescata da una modifica di configurazione. Per lo spazio già occupato c’è la conservazione, che è una decisione separata.')}
      </p>
    </Card>
  )
}
